// src/pages/CRM/Core/LeadsList.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
  Breadcrumbs,
  Link,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
  TrendingUp as LeadIcon,
  SwapHoriz as ConvertIcon,
  Whatshot as HotIcon,
  AcUnit as ColdIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../../utils/axiosInstance';
import { hasPermission } from '../../../../utils/permissionUtils';
import {
  LeadStatus,
  LeadStatusLabels,
  LeadStatusColors,
  LeadScore,
  LeadScoreLabels,
  LeadScoreColors,
  LeadSource,
  LeadSourceLabels,
} from './crmEnums';

const LeadsList = () => {
  const navigate = useNavigate();
  const { chatbotId } = useParams();

  // Permission checks
  const [canView, setCanView] = useState(false);
  const [canAdd, setCanAdd] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  // State
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    score: '',
    source: '',
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // Form state for create
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_name: '',
    job_title: '',
    source: LeadSource.MANUAL_ENTRY,
    status: LeadStatus.NEW,
    score: LeadScore.WARM,
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Convert form state
  const [convertForm, setConvertForm] = useState({
    create_account: true,
  });
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    checkPermissions();
  }, []);

  const checkPermissions = async () => {
    const view = await hasPermission('crm', 'view');
    const add = await hasPermission('crm', 'add');
    const edit = await hasPermission('crm', 'edit');
    const del = await hasPermission('crm', 'delete');
    setCanView(view);
    setCanAdd(add);
    setCanEdit(edit);
    setCanDelete(del);
  };

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    if (!canView) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page + 1,
        per_page: rowsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      if (chatbotId) params.append('chatbot_id', chatbotId); // Added chatbot_id
      if (searchQuery) params.append('search', searchQuery);
      if (filters.status) params.append('status', filters.status);
      if (filters.score) params.append('score', filters.score);
      if (filters.source) params.append('source', filters.source);

      const response = await axiosInstance.get(
        `/api/crm/leads?${params.toString()}`
      );

      if (response.data.success) {
        setLeads(response.data.data || []); // Fixed: response.data.data (standard), fallback to []
        setTotalCount(response.data.pagination?.total || 0); // Fixed: response.data.pagination.total
      } else {
        setError(response.data.message || 'Failed to fetch leads');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  }, [canView, page, rowsPerPage, sortBy, sortOrder, searchQuery, filters, chatbotId]);

  useEffect(() => {
    if (canView) {
      fetchLeads();
    }
  }, [fetchLeads, canView]);

  // Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      score: '',
      source: '',
    });
    setSearchQuery('');
    setPage(0);
  };

  const handleMenuOpen = (event, lead) => {
    setAnchorEl(event.currentTarget);
    setSelectedLead(lead);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewLead = () => {
    if (selectedLead) {
      navigate(`/chatbot/${chatbotId}/crm/leads/${selectedLead.id}`);
    }
    handleMenuClose();
  };

  const handleEditLead = () => {
    if (selectedLead) {
      navigate(`/chatbot/${chatbotId}/crm/leads/${selectedLead.id}?edit=true`);
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleConvertClick = () => {
    setConvertDialogOpen(true);
    setConvertForm({ create_account: true });
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedLead) return;

    try {
      const response = await axiosInstance.delete(
        `/api/crm/leads/${selectedLead.id}`
      );

      if (response.data.success) {
        fetchLeads();
        setDeleteDialogOpen(false);
        setSelectedLead(null);
      } else {
        setError(response.data.message || 'Failed to delete lead');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete lead');
    }
  };

  const handleConvertConfirm = async () => {
    if (!selectedLead) return;

    setConverting(true);
    try {
      const response = await axiosInstance.post(
        `/api/crm/leads/${selectedLead.id}/convert`,
        convertForm
      );

      if (response.data.success) {
        fetchLeads();
        setConvertDialogOpen(false);
        setSelectedLead(null);
        // Navigate to the new contact
        if (response.data.data?.contact?.id) {
          navigate(`/chatbot/${chatbotId}/crm/contacts/${response.data.data.contact.id}`);
        }
      } else {
        setError(response.data.message || 'Failed to convert lead');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to convert lead');
    } finally {
      setConverting(false);
    }
  };

  // Create lead handlers
  const handleCreateDialogOpen = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company_name: '',
      job_title: '',
      source: LeadSource.MANUAL_ENTRY,
      status: LeadStatus.NEW,
      score: LeadScore.WARM,
    });
    setFormErrors({});
    setCreateDialogOpen(true);
  };

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company_name: '',
      job_title: '',
      source: LeadSource.MANUAL_ENTRY,
      status: LeadStatus.NEW,
      score: LeadScore.WARM,
    });
    setFormErrors({});
  };

  const handleFormChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.first_name.trim()) {
      errors.first_name = 'First name is required';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      // Added chatbot_id to request parameters
      const params = chatbotId ? `?chatbot_id=${chatbotId}` : '';
      const response = await axiosInstance.post(`/api/crm/leads${params}`, formData);

      if (response.data.success) {
        handleCreateDialogClose();
        fetchLeads();
      } else {
        setFormErrors({ submit: response.data.message || 'Failed to create lead' });
      }
    } catch (err) {
      setFormErrors({
        submit: err.response?.data?.message || 'Failed to create lead',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Helper functions
  const getFullName = (lead) => {
    return [lead.first_name, lead.last_name].filter(Boolean).join(' ') || 'Unnamed Lead';
  };

  const getInitials = (lead) => {
    const first = lead.first_name?.[0] || '';
    const last = lead.last_name?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  const getScoreIcon = (score) => {
    if (score === LeadScore.HOT) return <HotIcon color="error" />;
    if (score === LeadScore.COLD) return <ColdIcon color="info" />;
    return null;
  };

  const getScoreProgress = (scoreNumeric) => {
    return scoreNumeric || 0;
  };

  if (!canView) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
        <Alert severity="error">
          You do not have permission to view leads.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate(`/chatbot/${chatbotId}/crm`)}
          >
            CRM
          </Link>
          <Typography color="text.primary">Leads</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm`)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              Leads
            </Typography>
            <Chip label={totalCount} color="primary" size="small" />
          </Box>
          {canAdd && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleCreateDialogOpen}
            >
              Add Lead
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search leads..."
              value={searchQuery}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={handleFilterChange('status')}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {Object.values(LeadStatus).map((status) => (
                  <MenuItem key={status} value={status}>
                    {LeadStatusLabels[status]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Score</InputLabel>
              <Select
                value={filters.score}
                label="Score"
                onChange={handleFilterChange('score')}
              >
                <MenuItem value="">All Scores</MenuItem>
                {Object.values(LeadScore).map((score) => (
                  <MenuItem key={score} value={score}>
                    {LeadScoreLabels[score]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Source</InputLabel>
              <Select
                value={filters.source}
                label="Source"
                onChange={handleFilterChange('source')}
              >
                <MenuItem value="">All Sources</MenuItem>
                {Object.values(LeadSource).map((source) => (
                  <MenuItem key={source} value={source}>
                    {LeadSourceLabels[source]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Clear Filters">
                <IconButton onClick={clearFilters}>
                  <ClearIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh">
                <IconButton onClick={fetchLeads}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Leads Table */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell>Lead</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No leads found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                leads.map((lead) => (
                  <TableRow
                    key={lead.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() =>
                      navigate(`/chatbot/${chatbotId}/crm/leads/${lead.id}`)
                    }
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor:
                              lead.score === LeadScore.HOT
                                ? 'error.main'
                                : lead.score === LeadScore.COLD
                                ? 'info.main'
                                : 'warning.main',
                          }}
                        >
                          {getInitials(lead)}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {getFullName(lead)}
                          </Typography>
                          {lead.job_title && (
                            <Typography variant="body2" color="text.secondary">
                              {lead.job_title}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {lead.company_name && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon fontSize="small" color="action" />
                          <Typography variant="body2">{lead.company_name}</Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {lead.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon fontSize="small" color="action" />
                          <Typography variant="body2">{lead.email}</Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={LeadStatusLabels[lead.status]}
                        size="small"
                        color={LeadStatusColors[lead.status]}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
                        <Chip
                          icon={getScoreIcon(lead.score)}
                          label={LeadScoreLabels[lead.score]}
                          size="small"
                          color={LeadScoreColors[lead.score]}
                          variant="outlined"
                        />
                        {lead.score_numeric !== undefined && lead.score_numeric !== null && (
                          <Box sx={{ width: 60 }}>
                            <LinearProgress
                              variant="determinate"
                              value={getScoreProgress(lead.score_numeric)}
                              color={
                                lead.score_numeric >= 70
                                  ? 'error'
                                  : lead.score_numeric >= 40
                                  ? 'warning'
                                  : 'info'
                              }
                              sx={{ height: 6, borderRadius: 3 }}
                            />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {LeadSourceLabels[lead.source] || lead.source}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, lead);
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewLead}>
          <LeadIcon sx={{ mr: 1 }} fontSize="small" />
          View Details
        </MenuItem>
        {canEdit && (
          <MenuItem onClick={handleEditLead}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit
          </MenuItem>
        )}
        {canEdit && selectedLead && !selectedLead.is_converted && (
          <MenuItem onClick={handleConvertClick}>
            <ConvertIcon sx={{ mr: 1 }} fontSize="small" />
            Convert to Contact
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Create Lead Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={handleCreateDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Lead</DialogTitle>
        <DialogContent>
          {formErrors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors.submit}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={handleFormChange('first_name')}
                error={!!formErrors.first_name}
                helperText={formErrors.first_name}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={handleFormChange('last_name')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleFormChange('email')}
                error={!!formErrors.email}
                helperText={formErrors.email}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={handleFormChange('phone')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name"
                value={formData.company_name}
                onChange={handleFormChange('company_name')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Job Title"
                value={formData.job_title}
                onChange={handleFormChange('job_title')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={handleFormChange('status')}
                >
                  {Object.values(LeadStatus).map((status) => (
                    <MenuItem key={status} value={status}>
                      {LeadStatusLabels[status]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Score</InputLabel>
                <Select
                  value={formData.score}
                  label="Score"
                  onChange={handleFormChange('score')}
                >
                  {Object.values(LeadScore).map((score) => (
                    <MenuItem key={score} value={score}>
                      {LeadScoreLabels[score]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={formData.source}
                  label="Source"
                  onChange={handleFormChange('source')}
                >
                  {Object.values(LeadSource).map((source) => (
                    <MenuItem key={source} value={source}>
                      {LeadSourceLabels[source]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateDialogClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Create Lead'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Lead</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{selectedLead && getFullName(selectedLead)}</strong>?
            This action will disqualify the lead.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert Lead Dialog */}
      <Dialog
        open={convertDialogOpen}
        onClose={() => setConvertDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Convert Lead to Contact</DialogTitle>
        <DialogContent>
          {selectedLead && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Converting <strong>{getFullName(selectedLead)}</strong> will create a new Contact record.
              </Alert>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Create Account?</InputLabel>
                <Select
                  value={convertForm.create_account}
                  label="Create Account?"
                  onChange={(e) =>
                    setConvertForm((prev) => ({
                      ...prev,
                      create_account: e.target.value,
                    }))
                  }
                >
                  <MenuItem value={true}>
                    Yes - Create a new Account from "{selectedLead.company_name || 'Company'}"
                  </MenuItem>
                  <MenuItem value={false}>No - Contact only</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)} disabled={converting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConvertConfirm}
            disabled={converting}
            startIcon={converting ? <CircularProgress size={20} /> : <ConvertIcon />}
          >
            Convert Lead
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadsList;